import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import useTheme from "@mui/material/styles/useTheme";

import { neutral } from "app/themes/colors";
import {
  getAssignedTagTooltip,
  tagColor
} from "components/assigned-tags/assigned-tags.utils";
import type { AssignedTag } from "services/types/assigned-tag.type";

// Same geometry as the hero's context chips, so the two rows line up.
const CHIP_SX = {
  display: "inline-flex",
  alignItems: "center",
  px: 0.875,
  py: 0.375,
  borderRadius: "999px",
  border: `1px solid ${neutral[200]}`,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.3,
  whiteSpace: "nowrap",
  maxWidth: 160,
  overflow: "hidden",
  textOverflow: "ellipsis"
} as const;

// 12px is the smallest thing the front desk should have to read; the dot is
// focusable so its name opens on keyboard focus as well as hover.
const DOT_SX = {
  display: "inline-block",
  width: 12,
  height: 12,
  borderRadius: "50%",
  border: `1px solid ${neutral[200]}`,
  flexShrink: 0,
  "&:focus-visible": { outline: `2px solid ${neutral[800]}`, outlineOffset: 1 }
} as const;

// The chip clips long names, so the hover text always leads with the name.
const chipTooltip = (assigned: AssignedTag) => {
  const details = getAssignedTagTooltip(assigned, false);
  return details ? `${assigned.tag.name} — ${details}` : assigned.tag.name;
};

// The patient's tags on a softphone call card, read-only and in the colours
// the practice knows them by. The full TagChip carries its edit pop-over and
// mutations, which a 320px call card has no room or reason for; this is the
// glanceable version. Past `limit` the rest fold into a "+N" chip that names
// them on hover.
//
// `dots` is the row-sized form: one colour dot per tag, named on hover and to
// assistive tech, in the line beside the patient's name. A row shares its
// card with other rows, or the panel with another call's hero, so it cannot
// spend a line of chips the way the hero can.
export default function SoftphonePatientTags({
  tags,
  limit,
  justify = "flex-start",
  dots = false
}: {
  tags: AssignedTag[];
  limit: number;
  justify?: "flex-start" | "center";
  dots?: boolean;
}) {
  const { palette } = useTheme();
  if (tags.length === 0) return null;

  const visible = tags.slice(0, limit);
  const hidden = tags.slice(limit);

  if (dots) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        component="span"
        data-testid="patient-tag-dots"
        sx={{ gap: 0.5, flexShrink: 0 }}
      >
        {visible.map((assigned) => (
          <Tooltip key={assigned.id} title={assigned.tag.name}>
            <Box
              component="span"
              role="img"
              tabIndex={0}
              aria-label={assigned.tag.name}
              data-testid="patient-tag-dot"
              sx={{ ...DOT_SX, bgcolor: tagColor(assigned.tag.color) }}
            />
          </Tooltip>
        ))}
        {hidden.length > 0 && (
          <Tooltip
            title={hidden.map((assigned) => assigned.tag.name).join(", ")}
          >
            <Box
              component="span"
              data-testid="patient-tags-more"
              sx={{ fontSize: 12, fontWeight: 600, color: neutral[800] }}
            >
              +{hidden.length}
            </Box>
          </Tooltip>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      useFlexGap
      flexWrap="wrap"
      justifyContent={justify}
      data-testid="patient-tags"
      sx={{ gap: 0.5 }}
    >
      {visible.map((assigned) => {
        const color = tagColor(assigned.tag.color);
        return (
          <Tooltip key={assigned.id} title={chipTooltip(assigned)}>
            <Box
              component="span"
              data-testid="patient-tag"
              sx={{
                ...CHIP_SX,
                bgcolor: color,
                color: palette.getContrastText(color)
              }}
            >
              {assigned.tag.name}
            </Box>
          </Tooltip>
        );
      })}
      {hidden.length > 0 && (
        <Tooltip title={hidden.map((assigned) => assigned.tag.name).join(", ")}>
          <Box
            component="span"
            data-testid="patient-tags-more"
            sx={{ ...CHIP_SX, bgcolor: neutral[100], color: neutral[800] }}
          >
            +{hidden.length}
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
}
